import { Link } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { ErrorState, LoadingBlock } from "../components/Feedback";
import { ProgressBar } from "../components/Gates";
import { Icon } from "../components/Icon";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { pluralise } from "../lib/format";
import type { CatalogueResponse } from "../lib/types";

type CatalogueCourse = CatalogueResponse["courses"][number];

const hours = (minutes: number) => `${Math.round(minutes / 60)} hours`;

function callToAction(course: CatalogueCourse): string {
  if (course.progress.certificate) return "Review the course";
  if (course.progress.modulesComplete === course.progress.modulesTotal) return "Claim your certificate";
  return course.progress.lessonsDone > 0 ? "Continue" : "Start the course";
}

function CourseCard({ course }: { course: CatalogueCourse }) {
  const started = course.progress.lessonsDone > 0 || course.progress.modulesComplete > 0;
  return (
    <article className="course-card" aria-labelledby={`course-${course.id}`}>
      <header className="course-card-head">
        <p className="mono course-card-engine">{course.engine ?? course.title}</p>
        <h2 id={`course-${course.id}`}>
          <Link to={`/courses/${course.id}`}>{course.title}</Link>
        </h2>
        <p className="course-card-tagline">{course.tagline}</p>
      </header>

      <p className="course-card-summary">{course.summary}</p>

      <dl className="course-card-stats">
        <div>
          <dt>Modules</dt>
          <dd>{course.stats.modules}</dd>
        </div>
        <div>
          <dt>Lessons</dt>
          <dd>{course.stats.lessons}</dd>
        </div>
        <div>
          <dt>Graded labs</dt>
          <dd>{course.stats.labs}</dd>
        </div>
        <div>
          <dt>About</dt>
          <dd>{hours(course.stats.minutes)}</dd>
        </div>
      </dl>

      <div className="course-card-progress">
        <div className="overall-line">
          <span>
            {started ? (
              <>
                <strong>{course.progress.modulesComplete}</strong> of{" "}
                <strong>{course.progress.modulesTotal}</strong> modules complete,{" "}
                {pluralise(course.progress.lessonsDone, "lesson")} read
              </>
            ) : (
              <span className="dim">Not started</span>
            )}
          </span>
          {course.progress.certificate ? (
            <Link to={`/courses/${course.id}/certificate`} className="pill pill-pass">
              Certificate {course.progress.certificate.id}
            </Link>
          ) : null}
        </div>
        <ProgressBar
          percent={course.progress.percent}
          label={`${course.progress.percent}% of ${course.title} complete`}
          done={course.progress.percent === 100}
        />
      </div>

      <div className="row">
        <Link className="btn btn-primary" to={`/courses/${course.id}`}>
          {callToAction(course)}
        </Link>
        <Link className="btn btn-quiet btn-sm" to={`/courses/${course.id}/certificate`}>
          <Icon name="award" />
          Certificate
        </Link>
      </div>
    </article>
  );
}

/** The signed-in home: every course on the platform, with the learner's progress through each. */
export default function Catalogue() {
  const { data, loading, error, reload } = useAsync((signal) => api.catalogue(signal), []);

  return (
    <AppShell>
      {loading && !data ? (
        <LoadingBlock label="Loading the courses" />
      ) : error && !data ? (
        <ErrorState error={error} onRetry={() => void reload()} title="The courses did not load" />
      ) : data ? (
        <>
          <header className="section-head catalogue-head">
            <h1>Courses</h1>
            <p className="dim">
              Each course is lessons, a quiz and a graded lab in your own sandbox for every module,
              then a capstone. Finish one and its certificate is yours.
            </p>
          </header>
          <div className="catalogue">
            {data.courses.map((course) => <CourseCard course={course} key={course.id} />)}
          </div>
        </>
      ) : null}
    </AppShell>
  );
}
